import { useMemo, useState } from "react";

import { KpiTile, tonePct } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from "@/components/cinch/DataTable/DataTable";
import type { DeliveryRow, IncidentOut, OverviewOut } from "../api";
import { HealthLandscape } from "./HealthLandscape";
import { useFeedDefinitions, useLandscape } from "../api";

export function IntegrationsLandscapeTab({
  data,
  onOpenIncident,
  onOpenEntity,
}: {
  data: OverviewOut;
  onOpenIncident: (id: string) => void;
  onOpenEntity: (level: string, keyPath: string) => void;
}) {
  const landscape = useLandscape("L1");
  const defs = useFeedDefinitions();
  const [sort, setSort] = useState<DataTableSort | null>(null);

  const cadenceBySource = useMemo(
    () => new Map((defs.data ?? []).map((d) => [d.entity_source, d])),
    [defs.data],
  );
  const cadenceOf = (r: DeliveryRow) => {
    const d = cadenceBySource.get(r.key_path);
    if (!d || d.cadence === "unknown") return null;
    return d.expected_interval_h != null
      ? `${d.cadence} (~${d.expected_interval_h}h)`
      : d.cadence;
  };

  // Client-side sort (jrae 2026-08-20 review) — the delivery list is one
  // page, so no server round-trip; accessor per key, nulls always last.
  const sortedDelivery = useMemo(() => {
    if (!sort) return data.delivery;
    const value = (r: DeliveryRow): string | number | null => {
      switch (sort.key) {
        case "cadence": {
          const d = cadenceBySource.get(r.key_path);
          return d && d.cadence !== "unknown" ? d.cadence : null;
        }
        case "last_arrival_at":
          return r.last_arrival_at ? Date.parse(r.last_arrival_at) : null;
        case "lag_p50_h":
          return r.lag_p50_h;
        case "maturity_age_days":
          return r.maturity_age_days;
        case "state":
          return r.state;
        case "profile_state":
          return r.profile_state;
        default:
          return r.key_path;
      }
    };
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...data.delivery].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [data.delivery, sort, cadenceBySource]);

  const deliveryColumns: DataTableColumn<DeliveryRow>[] = [
    { key: "key_path", header: "Integration", sortable: true },
    {
      key: "cadence",
      header: "Expected cadence",
      sortable: true,
      render: (r) => cadenceOf(r) ?? <span className="text-muted-foreground">learned</span>,
    },
    {
      key: "state",
      header: "Delivery",
      sortable: true,
      render: (r) => <StatusBadge value={r.state} />,
    },
    {
      key: "last_arrival_at",
      header: "Last arrival",
      sortable: true,
      render: (r) =>
        r.last_arrival_at ? new Date(r.last_arrival_at).toLocaleString() : "—",
    },
    {
      key: "lag_p50_h",
      header: "Typical lag",
      numeric: true,
      sortable: true,
      render: (r) => (r.lag_p50_h != null ? `${Math.round(r.lag_p50_h)}h` : "—"),
    },
    {
      key: "maturity_age_days",
      header: "Matures at",
      numeric: true,
      sortable: true,
      render: (r) =>
        r.maturity_age_days != null ? `${r.maturity_age_days}d` : "—",
    },
    {
      key: "profile_state",
      header: "State",
      sortable: true,
      render: (r) => <StatusBadge value={r.profile_state} />,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}
      >
        <KpiTile label="Integrations healthy" value={data.healthy} tone="positive" />
        <KpiTile
          label="Open incidents"
          value={data.open_incidents}
          tone={data.open_incidents === 0 ? "positive" : "danger"}
          sub={data.critical_incidents > 0 ? `${data.critical_incidents} critical` : undefined}
        />
        <KpiTile
          label="Delivering late"
          value={data.delivering_late}
          tone={data.delivering_late === 0 ? "positive" : "warning"}
        />
        <KpiTile
          label="Dormant"
          value={data.dormant}
          tone={data.dormant === 0 ? "neutral" : "warning"}
        />
        <KpiTile label="New this week" value={data.new_this_week} tone="neutral" />
        <KpiTile
          label="Early signals"
          value={data.pending_signals}
          tone={tonePct(100 - data.pending_signals * 10, 90, 60)}
        />
      </div>

      {landscape.data && (
        <HealthLandscape
          points={landscape.data}
          onPick={(p) => p.key_path && onOpenEntity(p.level ?? "L1", p.key_path)}
        />
      )}

      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Delivery
        </h2>
        <DataTable
          columns={deliveryColumns}
          rows={sortedDelivery}
          rowKey={(r) => r.key_path}
          sort={sort}
          onSortChange={setSort}
          onRowClick={(r) => onOpenEntity("L1", r.key_path)}
          emptyMessage="No integrations profiled yet — waiting on the first nightly run."
        />
      </div>

      {data.dormant_entities.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Dormant — was material, now flat
          </h2>
          <div className="space-y-1.5">
            {data.dormant_entities.map((i: IncidentOut) => (
              <button
                key={i.id}
                type="button"
                onClick={() => onOpenIncident(i.id)}
                className="flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <StatusBadge value="dormant" />
                <span className="font-medium">{i.key_path}</span>
                <span className="text-muted-foreground">
                  dormant since {i.last_bad_day ?? i.opened_on}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
